import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiArrowRight,
  FiLoader,
  FiMessageSquare,
  FiHome,
  FiMoon,
  FiPlus,
  FiSettings,
  FiShoppingBag,
  FiShoppingCart,
  FiSun,
  FiTrash2,
  FiUser,
  FiUsers,
} from 'react-icons/fi';
import { useTitle } from '../contexts/TitleContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useBasketSummary } from '../hooks/useCart';
import { cn, IconButton } from '../design-system';
import { useChats, useDeleteChat } from '../hooks/useChats';
import { returnToBasalam } from '../services/webviewService';

const navigation = [
  { href: '/', label: 'خانه', icon: FiHome },
  { href: '/settings', label: 'تنظیمات', icon: FiSettings },
];

export const ChatHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { title } = useTitle();
  const { theme, toggleTheme } = useTheme();
  const { userInfo } = useAuth();
  const { data: basketSummary } = useBasketSummary(Boolean(userInfo?.is_authenticated));
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isAuthenticated = Boolean(userInfo?.is_authenticated);
  const authenticatedUserId = isAuthenticated ? userInfo?.user_id : undefined;
  const { data: chats = [], isLoading: chatsLoading, isError: chatsError } = useChats(authenticatedUserId);
  const deleteChatMutation = useDeleteChat(authenticatedUserId);

  const isActive = (path: string) => path === '/'
    ? location.pathname === '/'
    : location.pathname.startsWith(path);

  const cartCount = basketSummary?.item_count || 0;
  const activeChatId = location.pathname.match(/^\/chat\/(\d+)/)?.[1];

  const handleDeleteChat = async (chatId: string) => {
    if (!window.confirm('این گفت‌وگو از فهرستت حذف بشه؟')) return;
    try {
      await deleteChatMutation.mutateAsync(Number(chatId));
      if (activeChatId === chatId) navigate('/');
    } catch (error) {
      console.error('Failed to delete chat:', error);
      window.alert('حذف گفت‌وگو انجام نشد؛ دوباره تلاش کن.');
    }
  };

  return (
    <>
      <header className="col-span-full row-start-1 z-20 flex h-[58px] min-w-0 items-center gap-2.5 border-b border-ds-100 bg-ds-white px-3 app-sm:h-[72px] app-sm:gap-5 app-sm:px-4 app-lg:px-6">
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={returnToBasalam}
            className="grid h-6 w-6 shrink-0 place-items-center rounded-ds-4 border-0 bg-transparent text-ds-600 transition-transform hover:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-primary active:scale-95"
            aria-label="بازگشت به باسلام"
            title="بازگشت به باسلام"
          >
            <FiArrowRight size={24} aria-hidden="true" />
          </button>

          <Link to="/" className="flex shrink-0 items-center gap-3" aria-label="دستیار باسلام">
            <span className="hidden text-[22px] font-black tracking-[-1px] text-ds-primary app-lg:inline">باسلام</span>
            <span className="hidden h-6 w-px bg-ds-100 app-lg:block" aria-hidden="true" />
            <span className="flex items-center gap-2 text-xs font-bold text-ds-900 app-sm:text-[13px]">
              <span className="relative grid h-[30px] w-[30px] place-items-center rounded-ds-8 border border-ds-100 bg-ds-white text-ds-navy app-sm:h-8 app-sm:w-8">
                <FiShoppingBag size={17} />
                <i className="absolute left-[5px] top-[5px] h-1 w-1 rounded-full bg-ds-primary" />
              </span>
              <span>دستیار باسلام</span>
              <small className="hidden rounded-ds-4 bg-ds-subtle px-1.5 py-0.5 text-[10px] font-medium text-ds-600 app-sm:inline">بتا</small>
            </span>
          </Link>
        </div>

        <p className="hidden min-w-0 truncate text-[13px] font-bold text-ds-900 app-lg:block">{title || 'شروع گفت‌وگو'}</p>

        <div className="mr-auto flex items-center gap-0 app-sm:gap-1">
          <IconButton onClick={toggleTheme} aria-label="تغییر پوسته" className="hidden app-sm:grid">
            {theme === 'dark' ? <FiSun size={20} /> : <FiMoon size={20} />}
          </IconButton>
          <a
            href="https://basalam.com/cart"
            target="_blank"
            rel="noopener noreferrer"
            className="relative grid h-9 w-9 shrink-0 place-items-center rounded-ds-6 text-ds-700 transition-colors hover:bg-ds-subtle app-sm:h-10 app-sm:w-10"
            aria-label="سبد خرید"
          >
            <FiShoppingCart size={20} />
            {cartCount > 0 && (
              <span className="absolute right-0 top-0 grid h-[17px] min-w-[17px] place-items-center rounded-full bg-ds-primary px-1 text-[10px] text-white">
                {cartCount > 9 ? '9+' : cartCount.toLocaleString('fa-IR')}
              </span>
            )}
          </a>
          {userInfo?.is_authenticated && (
            <div className="hidden h-10 items-center gap-2 rounded-ds-8 border border-ds-100 bg-ds-white py-0 pl-2.5 pr-[7px] text-xs text-ds-800 app-sm:flex">
              {userInfo.avatar ? (
                <img className="h-7 w-7 rounded-full object-cover" src={userInfo.avatar} alt={userInfo.name || 'کاربر'} />
              ) : (
                <span className="grid h-7 w-7 place-items-center rounded-full bg-[#f0d8ca] text-[#744832]"><FiUser size={15} /></span>
              )}
              <span className="hidden max-w-28 truncate app-lg:block">{userInfo.name || `کاربر #${userInfo.user_id}`}</span>
            </div>
          )}
        </div>
      </header>

      <aside className="col-start-1 row-start-2 hidden min-h-0 flex-col overflow-hidden border-l border-ds-100 bg-ds-white px-3 py-5 app-lg:flex">
        <div className="flex items-center justify-between px-2 pb-3">
          <strong className="text-[13px] text-ds-ink">منوی دستیار</strong>
          <span className="text-[11px] text-ds-500">دستیار خرید</span>
        </div>

        <Link
          to="/"
          className="flex h-11 w-full items-center justify-center gap-2 rounded-ds-8 bg-ds-primary text-[13px] font-bold text-white transition-colors hover:bg-ds-primary-strong"
        >
          <FiPlus size={18} />
          گفت‌وگوی جدید
        </Link>

        <nav className="mt-5 grid gap-1" aria-label="ناوبری اصلی">
          <p className="px-2 pb-1 text-[11px] text-ds-500">دسترسی سریع</p>
          {navigation.map(({ href, label, icon: Icon }) => (
            <Link
              key={href}
              to={href}
              className={cn(
                'flex h-10 items-center gap-2.5 rounded-ds-6 px-2.5 text-xs text-ds-700 transition-colors hover:bg-ds-subtle hover:text-ds-ink',
                isActive(href) && 'bg-ds-subtle font-bold text-ds-ink shadow-[inset_-2px_0_rgb(var(--ds-primary))]',
              )}
            >
              <Icon size={18} />
              <span>{label}</span>
            </Link>
          ))}
          {isDevelopment && (
            <>
              <Link
                to="/agents/compare"
                className={cn(
                  'flex h-10 items-center gap-2.5 rounded-ds-6 px-2.5 text-xs text-ds-700 transition-colors hover:bg-ds-subtle hover:text-ds-ink',
                  isActive('/agents/compare') && 'bg-ds-subtle font-bold text-ds-ink shadow-[inset_-2px_0_rgb(var(--ds-primary))]',
                )}
              >
                <FiUsers size={18} />
                <span>مقایسه دستیارها</span>
                <small className="mr-auto text-[9px] text-ds-500">توسعه</small>
              </Link>
            </>
          )}
        </nav>

        {isAuthenticated && (
          <section className="mt-5 flex min-h-0 flex-1 flex-col border-t border-ds-100 pt-4" aria-labelledby="chat-history-title">
            <div className="flex items-center justify-between px-2 pb-2">
              <p id="chat-history-title" className="text-[11px] text-ds-500">تاریخچه گفت‌وگوها</p>
              {chats.length > 0 && <span className="text-[10px] text-ds-400">{chats.length.toLocaleString('fa-IR')}</span>}
            </div>

            <div className="ds-scrollbar min-h-0 overflow-y-auto">
              {chatsLoading ? (
                <div className="flex items-center gap-2 px-2 py-3 text-[11px] text-ds-500">
                  <FiLoader className="animate-spin" size={14} /> در حال بارگذاری...
                </div>
              ) : chatsError ? (
                <p className="px-2 py-3 text-[11px] leading-5 text-ds-danger">تاریخچه بارگذاری نشد.</p>
              ) : chats.length === 0 ? (
                <p className="px-2 py-3 text-[11px] leading-5 text-ds-500">هنوز گفت‌وگویی نداری.</p>
              ) : (
                <ul className="grid gap-1">
                  {chats.map((chat) => {
                    const isCurrent = activeChatId === chat.id;
                    return (
                      <li key={chat.id} className={cn('group flex min-w-0 items-center rounded-ds-6', isCurrent && 'bg-ds-primary-soft')}>
                        <Link
                          to={`/chat/${chat.id}`}
                          className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-[11px] text-ds-700 hover:text-ds-ink"
                          title={chat.title}
                        >
                          <FiMessageSquare className="shrink-0 text-ds-400" size={14} />
                          <span className={cn('truncate', isCurrent && 'font-bold text-ds-primary-strong')}>{chat.title}</span>
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDeleteChat(chat.id)}
                          disabled={deleteChatMutation.isPending}
                          className="ml-1 grid h-8 w-8 shrink-0 place-items-center rounded-ds-6 text-ds-400 opacity-70 transition-colors hover:bg-red-50 hover:text-ds-danger focus:opacity-100 group-hover:opacity-100 disabled:opacity-30"
                          aria-label={`حذف گفت‌وگوی ${chat.title}`}
                          title="حذف گفت‌وگو"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        )}

        <div className="mt-3 border-t border-ds-100 pt-3">
          <p className="px-2 text-[10px] leading-5 text-ds-500">پاسخ‌ها رو همراه مشخصات محصول و شرایط خرید بررسی کن.</p>
        </div>
      </aside>
    </>
  );
};
